import { isFrameLikeElement, isTextElement } from "@excalidraw/element";

import type { ExcalidrawElement } from "@excalidraw/element/types";

export type LayerNode = {
  /** group nodes use the GroupId; element/frame nodes use the element id */
  id: string;
  kind: "element" | "group" | "frame";
  /** undefined for group nodes */
  element?: ExcalidrawElement;
  label: string;
  /** custom-named? (controls default-label styling) */
  named: boolean;
  hidden: boolean;
  locked: boolean;
  /** topmost first (reverse z-order), matching Figma */
  children: LayerNode[];
};

const TYPE_LABELS: Record<string, string> = {
  rectangle: "Rectangle",
  ellipse: "Ellipse",
  diamond: "Diamond",
  arrow: "Arrow",
  line: "Line",
  freedraw: "Drawing",
  text: "Text",
  image: "Image",
  frame: "Frame",
  magicframe: "Frame",
  embeddable: "Embed",
  iframe: "Embed",
  selection: "Selection",
};

export const getDefaultLabel = (element: ExcalidrawElement): string => {
  if (element.name) {
    return element.name;
  }
  if (isTextElement(element) && element.text.trim()) {
    const text = element.text.trim().replace(/\n/g, " ");
    return text.length > 24 ? `${text.slice(0, 24)}…` : text;
  }
  return (
    TYPE_LABELS[element.type] ??
    element.type.charAt(0).toUpperCase() + element.type.slice(1)
  );
};

export const buildLayerTree = (
  elements: readonly ExcalidrawElement[],
  getGroupNameFn?: (groupId: string) => string | null,
): LayerNode[] => {
  const roots: LayerNode[] = [];
  const frameNodes = new Map<string, LayerNode>();
  const groupNodes = new Map<string, LayerNode>();

  const elementNode = (element: ExcalidrawElement): LayerNode => ({
    id: element.id,
    kind: isFrameLikeElement(element) ? "frame" : "element",
    element,
    label: getDefaultLabel(element),
    named: element.name != null,
    hidden: element.hidden,
    locked: element.locked,
    children: [],
  });

  // walk topmost-first so children arrays come out in Figma order
  for (let i = elements.length - 1; i >= 0; i--) {
    const element = elements[i];
    if (element.isDeleted || (isTextElement(element) && element.containerId)) {
      continue;
    }

    // resolve the sibling list this element's node belongs into:
    // frame (outermost) first, then groupIds shallowest→deepest
    let siblings = roots;
    if (element.frameId) {
      let frameNode = frameNodes.get(element.frameId);
      if (!frameNode) {
        // child encountered before its frame (frames sit below their
        // children in z-order) — create a placeholder, filled in when
        // the frame element itself is reached
        frameNode = {
          id: element.frameId,
          kind: "frame",
          label: "Frame",
          named: false,
          hidden: false,
          locked: false,
          children: [],
        };
        frameNodes.set(element.frameId, frameNode);
        roots.push(frameNode);
      }
      siblings = frameNode.children;
    }
    // groupIds is ordered deepest→shallowest; nest from the outside in
    for (let g = element.groupIds.length - 1; g >= 0; g--) {
      const groupId = element.groupIds[g];
      let groupNode = groupNodes.get(groupId);
      if (!groupNode) {
        const groupName = getGroupNameFn?.(groupId) ?? null;
        groupNode = {
          id: groupId,
          kind: "group",
          label: groupName ?? "Group",
          named: groupName != null,
          hidden: false,
          locked: false,
          children: [],
        };
        groupNodes.set(groupId, groupNode);
        siblings.push(groupNode);
      }
      siblings = groupNode.children;
    }

    if (isFrameLikeElement(element)) {
      const existing = frameNodes.get(element.id);
      if (existing) {
        // fill in the placeholder created by an earlier child
        existing.element = element;
        existing.label = getDefaultLabel(element);
        existing.named = element.name != null;
        existing.hidden = element.hidden;
        existing.locked = element.locked;
      } else {
        const node = elementNode(element);
        frameNodes.set(element.id, node);
        siblings.push(node);
      }
    } else {
      siblings.push(elementNode(element));
    }
  }

  // group rows reflect aggregate hidden/locked state of their members;
  // frame rows use the frame element's own flags
  const finalize = (node: LayerNode): void => {
    node.children.forEach(finalize);
    if (node.kind === "group" && node.children.length) {
      node.hidden = node.children.every((child) => child.hidden);
      node.locked = node.children.every((child) => child.locked);
    }
  };
  roots.forEach(finalize);
  return roots;
};

export const flattenVisibleNodes = (
  nodes: LayerNode[],
  expanded: ReadonlySet<string>,
): LayerNode[] => {
  const out: LayerNode[] = [];
  const walk = (list: LayerNode[]) => {
    for (const node of list) {
      out.push(node);
      if (node.children.length && expanded.has(node.id)) {
        walk(node.children);
      }
    }
  };
  walk(nodes);
  return out;
};

export const collectElementIds = (node: LayerNode): string[] => {
  const ids: string[] = [];
  const walk = (n: LayerNode) => {
    if (n.element) {
      ids.push(n.element.id);
    }
    n.children.forEach(walk);
  };
  walk(node);
  return ids;
};

/**
 * Ids a row maps to when it drives the *selection* — as opposed to
 * hide/lock/drag, which act on the whole subtree (`collectElementIds`).
 * A frame selects only itself, matching a canvas click on the frame: its
 * members move/delete with it implicitly, and selecting them explicitly
 * draws a selection box per child and breaks every single-element control
 * (the Design panel's Screen size/duration section, W/H, …).
 */
export const selectionElementIds = (node: LayerNode): string[] =>
  node.kind === "frame" && node.element
    ? [node.element.id]
    : collectElementIds(node);
